import { useMutation, useQueryClient } from '@tanstack/react-query'

import { dataOrThrow, type PostAppointmentBody } from '@snootsvet/shared/portal'

import { usePortalApi } from '@/customer/hooks/use-api'
import { availableSlotsQueryKey } from '@/customer/hooks/use-get-available-slots'
import { appointmentsQueryKey } from '@/customer/hooks/use-list-appointments'
import { captureEvent } from '@/customer/lib/posthog'

/**
 * Books a portal appointment via `POST /api/portal/appointments`.
 *
 * On success, invalidates the upcoming-appointments list so the new
 * visit surfaces on the account page immediately, and the
 * available-slots cache so the newly-taken slot disappears from any
 * still-mounted picker.
 */
export function useBookAppointment() {
  const api = usePortalApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: PostAppointmentBody) =>
      dataOrThrow(await api.post('/appointments', { body })),
    onSuccess: ({ appointment }) => {
      queryClient.invalidateQueries({ queryKey: appointmentsQueryKey })
      queryClient.invalidateQueries({ queryKey: availableSlotsQueryKey })
      captureEvent('appointment_booked', {
        source: 'portal',
        appointment_id: appointment.id,
        patient_id: appointment.patientId,
        appointment_type_id: appointment.appointmentTypeId,
        clinic_id: appointment.clinicId,
      })
    },
  })
}
